import {
  LayoutServicePageState,
  Placeholder,
  Text,
  useSitecoreContext,
  Image,
  TextField,
} from '@sitecore-jss/sitecore-jss-nextjs';
import { useRouter } from 'next/router';
import classNames from 'classnames/bind';
import React, { useEffect, useState } from 'react';

import Button, { ButtonVariant } from 'components/helpers/Button/Button';
import Icon from 'components/helpers/Icon/Icon';
import Loader from 'components/helpers/Loader/Loader';

import compose from 'lib/enhancers/compose';
import withStyles from 'lib/enhancers/withStyles';

import mockPlanData from '../MockData/mock.data.plan-listings.json';
import styles from './PlanOptionsListingGraphQL.module.scss';

import {
  getQueryStringParameter,
  saveQueryStrings,
  getSavedPathWithQueryStrings,
  useDictionary,
  encryptDecrypt,
} from 'lib/utils';
import { buildURL, buildApplyToEnrollURL, checkEnrollEnable } from 'components/PlanData/utilities';
import { useI18n } from 'next-localization';
import { County } from '../Types/Counties.Type';
import {
  Plan,
  ListingFilter,
  ListingFilterOption,
  LoadingState,
  PlanOptionsListingProps,
  SortingOption,
  QueryStrings,
} from '../Types/Plans.Type';
import StarRating from '../StarRating';
import PlanListingFilter from './PlanListing.Filters';
import PlanListingFilterTags from './PlanListing.Filters.Tags';
import PlanListingSorting from './PlanListing.Sorting';
import { filterByCategory, updateFilterCounts } from './PlanListing.Functions';

const PlanOptionsListing = (props: PlanOptionsListingProps): React.JSX.Element => {
  const cx = classNames.bind(styles);
  const { t } = useI18n();
  const { sitecoreContext } = useSitecoreContext();
  const router = useRouter();

  const filteredResultsLabel = useDictionary('Plans.Filters.Header.Text', '{filtered} of {total} Medicare Plans');
  const planTypeLabel = useDictionary('PlanType');
  const monthlyPremiumLabel = useDictionary('MonthlyPremium');
  const maximumOutOfPocket = useDictionary('MaximumOutOfPocket');
  const annualDeductible = useDictionary('AnnualDeductible');
  const planHighlights = useDictionary('PlanHighlights');

  const { className, fields, rendering } = props;
  const { enableFilters, filters, sortingOptions } = fields;

  const [county, setCounty] = useState<County>();
  const [queryString, setQueryStrings] = useState<QueryStrings>({
    state: '',
    county: '',
    year: 0,
    zipcode: 0,
    existingMember: null,
    filters: [],
    sortBy: 'default',
  });
  const [baseURL, setBaseURL] = useState<string>('');
  const [plans, setPlans] = useState<Plan[]>([]);
  const [enableEnroll, setEnableEnroll] = useState(false);
  const [checked, setChecked] = useState<string[]>([]);
  const [currentFilters, setCurrentFilters] = useState<ListingFilter[] | undefined>(filters);
  const [useSortingOpitions, setUseSortingOptions] = useState<SortingOption[] | undefined>(sortingOptions);
  const [selectedFilterOptions, setSelectedFilterOptions] = useState<ListingFilterOption[]>([]);
  const [hasMatchedPlans, setHasMatchedPlans] = useState<boolean>(true);
  const [stage, setStage] = useState<LoadingState>(LoadingState.start);

  const planYear = React.useRef('');

  const queryStringKey = 'planoptions-qs';

  const compareErrorMessage = fields.compareError?.replaceAll('{max}', fields.maxCompare.toString());
  const compareSelectMessage = fields.compareSelect?.replaceAll('{max}', fields.maxCompare.toString());

  const checkSortingRating = (usePlans = plans) => {
    const hasStarRating = usePlans.some(
      (pln) => (pln.sortValues.find((x) => x.name == 'starRating')?.value ?? -1) >= 0
    );
    if (hasStarRating) {
      setUseSortingOptions(sortingOptions);
    } else {
      setUseSortingOptions(sortingOptions?.filter((sortOpts) => sortOpts.displayName != 'StarRating'));
    }
  };

  const updatePlansSortOrder = (sortingOption: SortingOption, plansToSort = plans) => {
    let urlName = '';
    plansToSort.sort((a, b) =>
      sortingOption.isAscending
        ? (a.sortValues.find((x) => x.name == sortingOption.fieldName)?.value ?? 0) -
          (b.sortValues.find((x) => x.name == sortingOption.fieldName)?.value ?? 0)
        : (b.sortValues.find((x) => x.name == sortingOption.fieldName)?.value ?? 0) -
          (a.sortValues.find((x) => x.name == sortingOption.fieldName)?.value ?? 0)
    );

    if (sortingOption.fieldName !== 'sitecoreTree') {
      urlName = sortingOption.fieldName;
    }
    if (queryString) {
      queryString.sortBy = urlName;
      setQueryStrings(queryString);
      updateURL();
    }
  };

  const filterPlans = (usedFilters = currentFilters, plansToFilter = plans) => {
    let tempPlans = plansToFilter;
    tempPlans.forEach((plan) => (plan.matchFilters = true));
    if (usedFilters) {
      usedFilters.forEach((filterCategory) => {
        const thisCategorySelections = filterCategory.options.filter((cat) => cat.selected);
        if (thisCategorySelections.length > 0) {
          tempPlans = filterByCategory(tempPlans, filterCategory);
        }
      });
      updateFilterCounts(usedFilters, tempPlans);
    }
    setPlans(tempPlans);
    setHasMatchedPlans(tempPlans.filter((x) => x.matchFilters).length > 0);
    return tempPlans;
  };

  const removeOptionFromFilter = (optionId: string) => {
    if (!currentFilters || currentFilters.length === 0) {
      return;
    }

    const $tempFilters = currentFilters;

    for (let x = 0; x < $tempFilters.length; x++) {
      let found = false;
      for (let y = 0; y < $tempFilters[x].options.length; y++) {
        if ($tempFilters[x].options[y].id == optionId) {
          $tempFilters[x].options[y].selected = false;
          found = false;
          break;
        }
      }
      if (found) break;
    }

    updateFilterCounts($tempFilters, plans);
    onFilterOptionSelectionChange($tempFilters);
  };

  const onFilterOptionSelectionChange = (updatedFilters: ListingFilter[]) => {
    const $selectedFilterIDs: string[] = [];
    const $selectedFilterOptions = updatedFilters.flatMap((filter) => {
      const tempFilters = filter.options.filter((option) => option.selected);
      tempFilters.map((tempOptions) => {
        if (tempOptions.selected) {
          $selectedFilterIDs.push(tempOptions.id);
        }
      });
      return tempFilters;
    });

    if (queryString) {
      queryString.filters = $selectedFilterIDs;
      setCurrentFilters(updatedFilters);
      setQueryStrings(queryString);
      updateURL();
    }
    setSelectedFilterOptions($selectedFilterOptions);
    filterPlans(updatedFilters);
  };

  const getUrlForPlanId = (planid: string) => {
    const countyName = queryString.county;
    const zipcode = queryString.zipcode.toString();
    const year = queryString.year.toString();
    const state = queryString.state;
    const cleanGUID = planid.replace(/[{}]/g, '');

    return buildURL(
      props.fields.detailpageurl,
      null,
      [
        ['planId', cleanGUID],
        ['county', countyName],
        ['year', year],
        ['zipcode', zipcode],
        ['state', state],
      ],
      sitecoreContext.language
    );
  };

  const handleSubmit = (planid: string) => {
    window.location.href = getUrlForPlanId(planid);
    return false;
  };

  const handleApplyToEnroll = async (planResponse: Plan) => {
    window.location.href = await buildApplyToEnrollURL({
      planTypeName: fields.planType.planName,
      zipcode: getQueryStringParameter('zipcode', router.query),
      county: getQueryStringParameter('county', router.query),
      year: getQueryStringParameter('year', router.query),
      planName: planResponse.planName,
      contract: planResponse.contract,
      pbp: planResponse.contract,
      groupID: planResponse.groupId,
      crmPrefix: fields.crmPrefix,
      mcProductID: planResponse.mcproductid?.toString(),
      cta: 'applytoenroll',
    });
    return false;
  };

  const handleCompare = () => {
    const planIDs: [string, string][] = Array(checked.length);

    for (let i = 0; i < checked.length; i++) {
      planIDs.push(['plan' + i, checked[i]]);
    }

    const url = buildURL(fields.comparepageurl, router.query, planIDs, sitecoreContext.language);
    window.location.href = url;

    return false;
  };

  const handleCheck = (planid: string) => {
    setChecked((prevState) => {
      // check if it is already added
      if (prevState.includes(planid)) {
        // clone the prevState arr to prevent side effects
        const clone = [...prevState];
        // Remove the existing id
        clone.splice(prevState.indexOf(planid), 1);
        return clone;
      } else {
        return [...prevState, planid];
      }
    });
  };

  const replaceTokens = (text?: string): string => {
    const count = plans.length?.toString();
    let countyName = queryString?.county ?? '';
    const planType = fields.planType?.planName ?? '';

    if (sitecoreContext.pageState !== LayoutServicePageState.Normal) {
      countyName = '[this]';
    }

    return text?.replaceAll('{count}', count).replaceAll('{county}', countyName).replaceAll('{type}', planType) ?? '';
  };

  const CheckQueryString = () => {
    if (queryString.zipcode != 0) {
      setStage(LoadingState.verify);
    } else if (
      router.asPath?.indexOf('?') < 0 ||
      router.asPath?.indexOf('planId=') > 0 ||
      router.asPath?.indexOf('plan0=') > 0
    ) {
      const savedUrl = getSavedPathWithQueryStrings(queryStringKey);
      const redirectPath = savedUrl || fields.searchpageurl;
      router.replace(redirectPath);
      setTimeout(() => {
        setStage(LoadingState.start);
      }, 500);
    } else {
      readQueryString();
    }
  };

  const readQueryString = () => {
    const newQueryString: QueryStrings = {
      county: '',
      state: '',
      year: 0,
      zipcode: 0,
      existingMember: false,
      filters: [],
      sortBy: '',
    };

    newQueryString.county = getQueryStringParameter('county', router.query);
    newQueryString.state = getQueryStringParameter('state', router.query);
    newQueryString.year = parseInt(getQueryStringParameter('year', router.query));
    newQueryString.zipcode = parseInt(getQueryStringParameter('zipcode', router.query));
    if (getQueryStringParameter('ExistingMember', router.query)) {
      newQueryString.existingMember = getQueryStringParameter('ExistingMember', router.query).toLowerCase() == 'true';
    } else {
      newQueryString.existingMember = null;
    }
    if (getQueryStringParameter('filters', router.query)) {
      newQueryString.filters = encryptDecrypt(getQueryStringParameter('filters', router.query), false).split('|');
    }
    if (getQueryStringParameter('sortBy', router.query)) {
      newQueryString.sortBy = getQueryStringParameter('sortBy', router.query);
    } else {
      newQueryString.sortBy = 'sitecoreTree';
    }
    setQueryStrings(newQueryString);
    setStage(LoadingState.verify);
  };

  /**
   * /**
   * Verify we have all 3 parameters required in the query string.
   * If we do, next need to validate we can find corresponding sitecore items.
   */
  const VerifyQueryStrings = () => {
    if (queryString.county && queryString.state && queryString.year > 0) {
      if (baseURL == '') {
        const [initialBaseUrl] = router.asPath.split('?');
        setBaseURL(initialBaseUrl);
      }
      planYear.current = getQueryStringParameter('year', router.query);

      const useCounty = fields.counties.find((cnty) => cnty.countyName == queryString.county);
      setCounty(useCounty);

      setEnableEnroll(checkEnrollEnable(fields.planYears, parseInt(planYear.current)));
      setStage(LoadingState.firstPass);
    } else {
      router.replace(fields.searchpageurl);
    }
  };

  /**
   * Remove all filter options that never match any of the plans returns
   */
  const RemoveUnusedFilters = () => {
    let matchingPlans: Plan[] = [];
    for (let x = 0; x < fields.plans.length; x++) {
      fields.plans[x].matchFilters = true;
      if (fields.plans[x].year != queryString.year) continue;
      for (let y = 0; y < fields.plans[x].counties.length; y++) {
        if (
          fields.plans[x].counties[y].name == queryString.county &&
          fields.plans[x].counties[y].state == queryString.state
        ) {
          matchingPlans.push(fields.plans[x]);
        }
      }
    }
    matchingPlans = filterPlans(fields.filters, matchingPlans);
    setPlans(matchingPlans);
    checkSortingRating(matchingPlans);
    if (queryString.sortBy != '' && sortingOptions) {
      const useOption =
        sortingOptions.find((sortOpt) => sortOpt.displayName == queryString.sortBy) ??
        sortingOptions.find((sortOpt) => sortOpt.fieldName == queryString.sortBy);
      if (useOption) {
        updatePlansSortOrder(useOption, matchingPlans);
      }
    }
    if (filters && queryString.filters && queryString.filters.length > 0) {
      setStage(LoadingState.secondPass);
    } else {
      setStage(LoadingState.end);
    }
  };

  /**
   * If there are filters passed in query string start with those selected and update accordingly
   */
  const InitializePassedFilters = () => {
    if (filters) {
      let tempFilters = filters;
      if (currentFilters != undefined) {
        tempFilters = currentFilters;
      }
      queryString.filters.forEach((fltrID) => {
        for (let x = 0; x < tempFilters.length; x++) {
          let found = false;
          for (let y = 0; y < tempFilters[x].options.length; y++) {
            if (tempFilters[x].options[y].id == fltrID) {
              tempFilters[x].options[y].selected = true;
              found = true;
              break;
            }
          }
          if (found) break;
        }
      });
      setCurrentFilters(tempFilters);

      onFilterOptionSelectionChange(tempFilters);
    }
    setStage(LoadingState.end);
  };

  useEffect(() => {
    switch (stage) {
      case LoadingState.start:
        if (router.isReady && sitecoreContext) {
          if (sitecoreContext.pageEditing) {
            setEnableEnroll(true);
            setPlans(mockPlanData as Plan[]);
            setStage(LoadingState.end);
          } else {
            setStage(LoadingState.query);
          }
        }
        break;
      case LoadingState.query:
        CheckQueryString();
        break;
      case LoadingState.verify:
        VerifyQueryStrings();
        break;
      case LoadingState.firstPass:
        RemoveUnusedFilters();
        break;
      case LoadingState.secondPass:
        InitializePassedFilters();
        break;
    }
  }, [stage, router.isReady]);

  useEffect(() => {
    if (stage == LoadingState.end) {
      saveQueryStrings(router, queryStringKey);
    }
  }, [router.asPath]);

  function updateURL() {
    let url = baseURL;
    if (queryString) {
      const queries = [];
      if (queryString.state) {
        queries.push('state=' + queryString.state);
      }
      if (queryString.county) {
        queries.push('county=' + queryString.county);
      }
      if (queryString.year) {
        queries.push('year=' + queryString.year);
      }
      if (queryString.zipcode) {
        queries.push('zipcode=' + queryString.zipcode);
      }
      if (queryString.existingMember != null) {
        queries.push('ExistingMember=' + queryString.existingMember);
      }
      if (queryString.filters.length > 0) {
        queries.push('filters=' + encryptDecrypt(queryString.filters.join('|'), true));
      }
      if (queryString.sortBy && queryString.sortBy != 'sitecoreTree') {
        queries.push('sortBy=' + queryString.sortBy);
      }
      url += '?' + queries.join('&');
    }
    router.replace(url, url, { scroll: false });
    saveQueryStrings(router, queryStringKey);
    setStage(LoadingState.verify);
  }

  return (
    <>
      <div role="status">
        {stage == LoadingState.end ? (
          <span className={cx('screen-reader-only')}>{t('Plan options loaded')}</span>
        ) : (
          <Loader />
        )}
      </div>
      {plans?.length > 0 && (
        <>
          <div className={cx('plan-options-listing__img-container')}>
            {county?.image ? <Image alt={county?.countyName} field={county?.image} /> : ''}
          </div>
          <header className={cx('plan-options-listing__results')}>
            <Text encode={false} field={{ value: replaceTokens(fields.title) }} tag="h2" />
            <Text encode={false} field={{ value: replaceTokens(fields.description) }} tag="p" />
          </header>
        </>
      )}

      <section className={cx('plan-options-listing', className, { 'plan-options-listing--filters': enableFilters })}>
        <aside className={cx('plan-options-listing__aside', { 'plan-options-listing__aside--filters': enableFilters })}>
          {enableFilters && (
            <PlanListingFilter
              plans={plans}
              filters={currentFilters || []}
              onFilterOptionSelectionChange={onFilterOptionSelectionChange}
              displayType="desktop"
            />
          )}
          {/* desktop only mobile after content */}
          <div className={cx('display-type-desktop')}>
            <Placeholder
              key={0}
              name="hp-container-3070-right"
              rendering={rendering}
              displayType="desktop"
              disableSuspense={true}
            />
          </div>
        </aside>
        {plans?.length > 0 && (
          <div className={cx('plan-options-listing__results-wrapper')}>
            {enableFilters && (
              <div className={cx('plan-options-listing--filters-header')}>
                <h3 className={cx('heading')} role="status">
                  {filteredResultsLabel
                    .replace('{total}', `${plans.length}`)
                    .replace('{filtered}', `${plans.filter((pln) => pln.matchFilters).length?.toString()}`)}
                </h3>

                <h3>
                  <PlanListingFilter
                    plans={plans}
                    filters={currentFilters || []}
                    onFilterOptionSelectionChange={onFilterOptionSelectionChange}
                    displayType="mobile"
                    sortingOptions={useSortingOpitions}
                    updatePlansSortOrder={updatePlansSortOrder}
                    defaultSelection={queryString?.sortBy}
                  />
                </h3>

                <PlanListingSorting
                  sortingOptions={useSortingOpitions}
                  updatePlansSortOrder={updatePlansSortOrder}
                  defaultSelection={queryString?.sortBy !== '' ? queryString?.sortBy : 'sitecoreTree'}
                  displayType="desktop"
                />

                <PlanListingFilterTags
                  removeOptionFromFilter={removeOptionFromFilter}
                  selectedFilterOptions={selectedFilterOptions}
                />
              </div>
            )}
            <ul className="plan-options-listing__plan" style={{ margin: 0, paddingLeft: 0, listStyle: 'none' }}>
              {hasMatchedPlans &&
                plans.map((plan, planIndex) => {
                  if (plan.matchFilters) {
                    const { featuredPlanEntries, planName } = plan;
                    const ratingValue = +(plan.sortValues.find((x) => x.name === 'starRating')?.value ?? 0);
                    const insurancePlanType =
                      plan.filters.find((y) => y.name == 'Insurance Plan Type')?.selectedNames ?? '';
                    const planKey = `${planName}-${planIndex}`;

                    return (
                      <li className={cx('plan-options-listing__plan')} key={planKey}>
                        <header className={cx('plan-options-listing__plan-header')}>
                          <a href={getUrlForPlanId(plan.id)}>
                            <h4 className={cx('plan-options-listing__plan-header-text')}>
                              <span dangerouslySetInnerHTML={{ __html: planName ? planName : '' }} />
                              <Icon className={cx('plan-options-listing__plan-header-carat')} variant="carat"></Icon>
                            </h4>
                          </a>
                          <StarRating
                            ratingValue={ratingValue}
                            className="plan-options-listing__plan-rating"
                            iconClassName="plan-options-listing__plan-rating-star"
                            itemKey={planKey}
                          />
                        </header>
                        <ul className={cx('plan-options-listing__plan-gray-header')}>
                          <li className={cx('plan-options-listing__plan-gray-header__item')}>
                            <span className={cx('plan-options-listing__plan-gray-header__label')}>{planTypeLabel}</span>
                            <span className={cx('plan-options-listing__plan-gray-header__value')}>
                              {insurancePlanType}
                            </span>
                          </li>
                          <li className={cx('plan-options-listing__plan-gray-header__item')}>
                            <span className={cx('plan-options-listing__plan-gray-header__label')}>
                              {monthlyPremiumLabel}
                            </span>
                            <span className={cx('plan-options-listing__plan-gray-header__value')}>
                              ${plan.sortValues?.find((x) => x.name == 'premium')?.value}
                            </span>
                          </li>
                          <li className={cx('plan-options-listing__plan-gray-header__item')}>
                            <span className={cx('plan-options-listing__plan-gray-header__label')}>
                              {maximumOutOfPocket}
                            </span>
                            <span className={cx('plan-options-listing__plan-gray-header__value')}>
                              {(() => {
                                const value = plan.sortValues?.find((x) => x.name === 'outOfPocket')?.value;
                                if (typeof value === 'number') {
                                  const formatted =
                                    value >= 1000 && value < 10000
                                      ? value.toString().replace(/(\d)(?=(\d{3})$)/, '$1,')
                                      : value.toString();
                                  return `$${formatted}`;
                                }
                                return `$${value}`;
                              })()}
                            </span>
                            <span className={cx('plan-options-listing__plan-gray-header__network-value')}>
                              {plan.outOfPocketScope}
                            </span>
                          </li>
                          <li className={cx('plan-options-listing__plan-gray-header__item')}>
                            <span className={cx('plan-options-listing__plan-gray-header__label')}>
                              {annualDeductible}
                            </span>
                            <span className={cx('plan-options-listing__plan-gray-header__value')}>
                              {(() => {
                                const value = plan.sortValues?.find((x) => x.name === 'deductible')?.value;
                                if (typeof value === 'number') {
                                  const formatted =
                                    value >= 1000 && value < 10000
                                      ? value.toString().replace(/(\d)(?=(\d{3})$)/, '$1,')
                                      : value.toString();
                                  return `$${formatted}`;
                                }
                                return `$${value}`;
                              })()}
                            </span>
                            <span className={cx('plan-options-listing__plan-gray-header__network-value')}>
                              {plan.annualDeductibleNetworkScope}
                            </span>
                          </li>
                        </ul>
                        <article className={cx('plan-options-listing__plan-content')}>
                          <h5 className={cx('font-style-sm', 'plan-options-listing__plan-entry-label-heading')}>
                            {planHighlights}
                          </h5>
                          <ul className={cx('plan-options-listing__plan-entries')}>
                            {featuredPlanEntries?.map((item, itemIndex) => {
                              const { groupEntry } = item;
                              const itemKey = `${planKey}-${groupEntry.label}-${itemIndex}`;
                              const groupLabel: TextField = { value: groupEntry.label ?? '' };
                              const entryValue: TextField = { value: item.value ?? '' };

                              return (
                                <li className={cx('plan-options-listing__plan-entry')} key={itemKey}>
                                  {groupEntry.icon ? (
                                    <img
                                      alt={groupEntry.alt}
                                      className={cx('plan-options-listing__plan-entry-icon')}
                                      src={groupEntry.icon}
                                    />
                                  ) : (
                                    ''
                                  )}

                                  <div
                                    className={
                                      !groupEntry.icon ? cx('plan-options-listing__plan-entry-label-no-image') : ''
                                    }
                                  >
                                    <Text
                                      className={cx('font-style-sm', 'plan-options-listing__plan-entry-label-heading')}
                                      editable={false}
                                      encode={false}
                                      field={groupLabel}
                                      tag="h6"
                                    />
                                    <Text editable={false} encode={false} field={entryValue} tag="p" />
                                  </div>
                                </li>
                              );
                            })}
                          </ul>
                        </article>
                        <footer className={cx('plan-options-listing__plan-footer')}>
                          <div className={cx('plan-options-listing__footer-buttons')}>
                            <Button
                              className={cx('plan-options-listing__view-button')}
                              onClick={() => {
                                handleSubmit(plan.id);
                              }}
                            >
                              {t('View Plan Details')}
                            </Button>
                            {enableEnroll && (
                              <Button
                                className={cx('plan-options-listing__apply-to-enroll-button')}
                                variant={ButtonVariant.Tertiary}
                                onClick={() => {
                                  handleApplyToEnroll(plan);
                                }}
                              >
                                {t('Apply to Enroll')}
                              </Button>
                            )}
                          </div>
                          <section className={cx('plan-options-listing__compare-section')}>
                            <div className={cx('plan-options-listing__compare-checkbox')}>
                              <input
                                type="checkbox"
                                id={`${plan.id}-checkbox`}
                                key={`${plan.id}-checkbox`}
                                checked={checked.indexOf(plan.id) > -1}
                                disabled={checked.length >= fields.maxCompare && checked.indexOf(plan.id) < 0}
                                onChange={() => handleCheck(plan.id)}
                              />
                              <label htmlFor={`${plan.id}-checkbox`}>{t('Compare')}</label>
                            </div>
                            <div
                              className={cx('plan-options-listing__compare-summary')}
                              role={planIndex === 0 ? 'status' : undefined}
                            >
                              {checked.length >= fields.maxCompare && checked.indexOf(plan.id) < 0 ? (
                                <span>{compareErrorMessage}</span>
                              ) : (
                                ''
                              )}
                              {checked.length === 1 ? <span>{compareSelectMessage}</span> : ''}
                              {checked.length > 1 && checked.indexOf(plan.id) > -1 ? (
                                <Button
                                  variant={ButtonVariant.Green}
                                  className={cx('plan-options-listing__view-button')}
                                  onClick={handleCompare}
                                >
                                  {t('Compare Plans')}
                                </Button>
                              ) : (
                                ''
                              )}
                            </div>
                          </section>
                        </footer>
                      </li>
                    );
                  } else {
                    return;
                  }
                })}
              {!hasMatchedPlans && (
                //TODO: No matching plans screen
                <div>OOPS!</div>
              )}
            </ul>
          </div>
        )}
      </section>
      {/* mobile and tablet only desktop aside */}
      <div className={cx('display-type-mobile')}>
        <Placeholder key={0} name="hp-container-3070-right" rendering={rendering} disableSuspense={true} />
      </div>
    </>
  );
};

/**
 * Will be called during SSG/ISR
 * ! We don't want to use this method because it's used for generating the component on the static page
 * @param {ComponentRendering} rendering
 * @param {LayoutServiceData} layoutData
 * @param {GetStaticPropsContext} context
 */

export default compose<PlanOptionsListingProps>(withStyles())(PlanOptionsListing);
